import { Button, Hidden, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material'
import withStyles from '@mui/styles/withStyles'
import moment from 'moment-timezone'
import React from 'react'
import { withAppContext } from '../context/AppContextProvider'

const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

const styles = (theme) => ({
    loading: {
        margin: theme.spacing(2),
        width: 200,
    },
    tableHeaderCell: {
        padding: theme.spacing(0.5),
        minWidth: 75,
    },
    tableHeaderCellRight: {
        textAlign: 'right',
    },
    tableCell: {
        border: 'none',
        padding: theme.spacing(0.5),
        verticalAlign: 'top',
        whiteSpace: 'nowrap',
    },
    weekButton: {
        padding: 5,
    },
    timeslotButton: {
        padding: '3px 5px',
    },
})

class AppointmentPicker extends React.Component {
    state = {
        waits: 1,
        availability: [],
    }

    componentDidMount = async () => {
        await this.getAvailability()

        await this.setState({
            waits: this.state.waits - 1,
        })
    }

    componentDidUpdate = async (prevProps) => {
        if (this.props.displayWeekStart.toString() != prevProps.displayWeekStart.toString()) {
            await this.getAvailability()
        }
    }

    getAvailability = async () => {
        await this.setState({
            waits: this.state.waits + 1,
        })

        try {
            let date = moment(this.props.displayWeekStart)
            let dates = []

            // get 7 dates starting at displayWeekStart
            for (let i = 0; i < 7; i++) {
                dates.push(
                    date.format('Y-MM-DD')
                )

                date.add(1, 'days')
            }

            const { availability, otherAppointments } = await this.props.socket.send('book', {
                step: 'getAppointmentAvailability',
                serviceId: this.props.serviceId,
                employeeId: this.props.employeeId,
                dates,
            })

            await this.setState({
                availability,
                otherAppointments,
            })

        } catch (err) {
            this.props.showError(err)
        }

        await this.setState({
            waits: this.state.waits - 1,
        })
    }

    handleSlotClick = async (date, time) => {
        if (this.props.onSlotClick) {
            this.props.onSlotClick(date, time)
        }
    }

    render = () => {
        const { classes, containerClass, hideUnavailableTimes, user, store } = this.props
        const { availability, otherAppointments, waits } = this.state

        if (waits > 0) {
            return (
                <TableContainer className={containerClass}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {Array.from({ length: 7 }, (e, i) => (
                                    <TableCell key={i} sx={{ p: 0.5, minWidth: 75 }} component="th" scope="row" align="center">
                                        <Skeleton sx={{ display: 'inline-block' }} width={30} height={25} /><br />
                                        <Skeleton sx={{ display: 'inline-block' }} width={65} height={25} />
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.from({ length: 10 }, (e, i) =>
                                <TableRow key={i}>
                                    {Array.from({ length: 7 }, (e, i) => (
                                        <TableCell key={i} className={classes.tableCell} sx={{ p: 0.8 }} align="center">
                                            <Skeleton sx={{ display: 'inline-block' }} width={60} height={25} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )
        }

        if (availability.length == 0) {
            return <div />
        }

        const sortedDates = Object.keys(availability).sort()
        const sortedTimes = Object.keys(availability[sortedDates[0]]).sort() // borrow time list from first day as it should be the same list for each day

        return (
            <TableContainer className={containerClass}>
                <Table stickyHeader size="small">
                    { hideUnavailableTimes == true && <>
                        <TableHead>
                            <TableRow>
                                {sortedDates.filter((date) => {
                                    const hasAvailability = Object.values(availability[date]).some(time => time !== false)
                                    const dateMoment = moment(date, 'Y-MM-DD')

                                    return hasAvailability && dateMoment.isBefore(moment(), 'day') == false
                                }).map((date) => {
                                    const dateMoment = moment(date, 'Y-MM-DD')

                                    return (
                                        <TableCell key={date} className={classes.tableHeaderCell} component="th" scope="row" align="center">
                                            {dateMoment.format('ddd')}<br />
                                            {dateMoment.format('MMM Do')}
                                        </TableCell>
                                    )
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                {sortedDates.filter((date) => {
                                    const hasAvailability = Object.values(availability[date]).some(time => time !== false)
                                    const dateMoment = moment(date, 'Y-MM-DD')

                                    return hasAvailability && dateMoment.isBefore(moment(), 'day') == false
                                }).map((date) => (
                                    <TableCell key={date} className={classes.tableCell} align="center">
                                        {sortedTimes.filter((time) => availability[date][time]).map((time) => {
                                            const localTimeFormatted = moment.tz(time, 'HH:mm', store.timezone).tz(localTimeZone).format('h:mm A')
                                            const showOther = user.isEmployee && otherAppointments && otherAppointments[date][time].length
                                            const tooltip = showOther ? <>{otherAppointments[date][time].length} other appointments during this slot:<br />{otherAppointments[date][time].map((time) => <>{time}<br /></>)}</> : 'Click to Book'

                                            return (
                                                <div key={`${date}-${time}`}>
                                                    <Tooltip disableInteractive arrow title={tooltip}>
                                                        <Button
                                                            className={classes.timeslotButton}
                                                            fullWidth
                                                            onClick={() => { this.handleSlotClick(date, time) }}
                                                        >
                                                            {localTimeFormatted}
                                                        </Button>
                                                    </Tooltip>
                                                </div>
                                            )
                                        })}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableBody>
                    </>}
                    { hideUnavailableTimes == false && <>
                        <TableHead>
                            <TableRow>
                                {sortedDates.map((date) => {
                                    const dateMoment = moment(date, 'Y-MM-DD')
                                    const cell = (
                                        <TableCell key={date} className={classes.tableHeaderCell} component="th" scope="row" align="center">
                                            {dateMoment.format('ddd')}<br />
                                            {dateMoment.format('MMM Do')}
                                        </TableCell>
                                    )

                                    if (dateMoment.isBefore(moment(), 'day')) {
                                        return <Hidden key={date} mdDown>{cell}</Hidden>
                                    } else {
                                        return cell
                                    }
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedTimes.map((time) => (
                                <TableRow key={time}>
                                    {sortedDates.map((date) => {
                                        const dateMoment = moment(date, 'Y-MM-DD')
                                        const localTimeFormatted = moment.tz(time, 'HH:mm', store.timezone).tz(localTimeZone).format('h:mm A')
                                        const showOther = user.isEmployee && otherAppointments && otherAppointments[date][time].length
                                        const tooltip = showOther ? <>{otherAppointments[date][time].length} other appointments during this slot:<br />{otherAppointments[date][time].map((apptTime, i) => <div key={i}>{apptTime}</div>)}</> : 'Click to Book'

                                        const cellContents = availability[date][time] ? (
                                            <Tooltip disableInteractive arrow title={tooltip}>
                                                <Button
                                                    className={classes.timeslotButton}
                                                    fullWidth
                                                    disabled={!availability[date][time]}
                                                    onClick={() => { this.handleSlotClick(date, time) }}
                                                >
                                                    {localTimeFormatted}
                                                </Button>
                                            </Tooltip>
                                        ) : (
                                            <Button
                                                className={classes.timeslotButton}
                                                fullWidth
                                                disabled={true}
                                            >
                                                {localTimeFormatted}
                                            </Button>
                                        )

                                        const cell = (
                                            <TableCell key={`${date}-${time}`} className={classes.tableCell} align="center">
                                                {cellContents}
                                            </TableCell>
                                        )

                                        if (dateMoment.isBefore(moment(), 'day')) {
                                            return <Hidden key={`${date}-${time}`} mdDown>{cell}</Hidden>
                                        } else {
                                            return cell
                                        }
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </>}
                </Table>
            </TableContainer>
        )
    }
}

export default withAppContext(withStyles(styles, { withTheme: true })(AppointmentPicker))
